import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion, type Variants } from "framer-motion";
import { ArrowUp, Check, Code2, Loader2, RotateCcw, X, Sparkles } from "lucide-react";
import { FormControlStyles } from "../../components/ui/FormControlStyles";
import { Header } from "../../features/kquant/components/Header";
import { ChatBar } from "../../features/kquant/components/ChatBar";
import { streamFinanceChat, type FinanceChatEvent } from "../../features/kquant/api/financeChat";
import { MessageContent } from "./MessageContent";
import { DemoPrompts } from "./DemoPrompts";
import { MarkdownRenderer } from "../../features/kquant/components/MarkdownRenderer";

type LocationState = {
  prompt?: string;
  seedId?: string;
};

type ToolCallState = {
  id: string;
  name: string;
  argumentsText: string;
  result?: string;
  state: "building" | "running" | "success" | "error";
};

type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  thinkingText?: string;
  toolCalls?: ToolCallState[];
};

type InitialTurnState = {
  uiEnqueued: boolean;
  sendStatus: "idle" | "scheduled" | "sent";
  assistantId: string;
};

const initialTurnStateByKey = new Map<string, InitialTurnState>();

function makeId(prefix: string) {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function summarizeToolArgs(text: string) {
  const raw = (text ?? "").trim();
  if (!raw) return "";
  try {
    const v = JSON.parse(raw) as unknown;
    if (!v || typeof v !== "object" || Array.isArray(v)) return "";
    const keys = Object.keys(v as Record<string, unknown>);
    if (keys.length === 0) return "";
    const shown = keys.slice(0, 3).join(", ");
    return keys.length > 3 ? `${shown}, …` : shown;
  } catch {
    return "";
  }
}

const toolListVariants: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.12 } },
};

const toolItemVariants: Variants = {
  hidden: { opacity: 0, filter: "blur(4px)", x: -8 },
  show: {
    opacity: 1,
    filter: "blur(0px)",
    x: 0,
    transition: { type: "spring" as const, stiffness: 400, damping: 30 },
  },
};

export default function SmartInsightsConversationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingAssistantId, setStreamingAssistantId] = useState<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const promptFromState = useMemo(() => {
    const st = (location.state as LocationState | null) ?? null;
    return (st?.prompt ?? "").trim();
  }, [location.state]);

  const promptFromQuery = useMemo(() => (searchParams.get("q") ?? "").trim(), [searchParams]);

  const initialPrompt = promptFromState || promptFromQuery;

  const initialSeedKey = useMemo(() => {
    const st = (location.state as LocationState | null) ?? null;
    const seedId = (st?.seedId ?? "").trim();
    if (seedId) return `seed:${seedId}`;
    const text = (initialPrompt ?? "").trim();
    return text ? `loc:${location.key}:${text}` : "";
  }, [initialPrompt, location.key, location.state]);

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
    setStreamingAssistantId(null);
  }, []);

  const handleReset = useCallback(() => {
    stopStreaming();
    setMessages([]);
    sessionIdRef.current = null;
    setInput("");
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, navigate, stopStreaming]);

  useEffect(() => {
    return () => {
      stopStreaming();
    };
  }, [stopStreaming]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, isStreaming]);

  const patchAssistant = useCallback((id: string, patch: (m: ChatMessage) => ChatMessage) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? patch(m) : m)));
  }, []);

  const startChatTurn = useCallback(
    async (text: string, opts?: { assistantId?: string; skipEnqueue?: boolean }) => {
      const t = (text ?? "").trim();
      if (!t) return;

      stopStreaming();

      const assistantId = opts?.assistantId ?? makeId("a");

      if (!opts?.skipEnqueue) {
        const userMsg: ChatMessage = { id: makeId("u"), role: "user", content: t };
        const assistantMsg: ChatMessage = {
          id: assistantId,
          role: "assistant",
          content: "",
          thinkingText: "",
          toolCalls: [],
        };
        setMessages((prev) => [...prev, userMsg, assistantMsg]);
      }
      setInput("");
      setIsStreaming(true);
      setStreamingAssistantId(assistantId);

      const controller = new AbortController();
      abortRef.current = controller;

      let localSessionId = sessionIdRef.current;
      let messageStageCount = 0;

      const onEvent = (evt: FinanceChatEvent | { type: "done" }) => {
        if (controller.signal.aborted) return;
        if ((evt as any)?.type === "done") {
          setIsStreaming(false);
          setStreamingAssistantId(null);
          return;
        }

        if ((evt as any)?.type === "meta" && typeof (evt as any)?.session_id === "string") {
          localSessionId = (evt as any).session_id;
          sessionIdRef.current = localSessionId;
          return;
        }

        if (typeof (evt as any)?.error === "string") {
          const errText = String((evt as any).error);
          patchAssistant(assistantId, (m) => ({ ...m, content: errText }));
          setIsStreaming(false);
          setStreamingAssistantId(null);
          return;
        }

        const tEvt = evt as any;

        if (tEvt.type === "stage" && typeof tEvt.stage === "string") {
          if (tEvt.stage === "message") {
            messageStageCount += 1;
            if (messageStageCount > 1) {
              patchAssistant(assistantId, (m) => {
                const cur = m.content ?? "";
                if (!cur.trim()) return m;
                if (cur.endsWith("\n")) return m;
                return { ...m, content: cur + "\n" };
              });
            }
          }
          return;
        }

        if (tEvt.type === "thinking" && typeof tEvt.content === "string") {
          patchAssistant(assistantId, (m) => ({ ...m, thinkingText: (m.thinkingText ?? "") + tEvt.content }));
          return;
        }

        if (tEvt.type === "message" && typeof tEvt.content === "string") {
          patchAssistant(assistantId, (m) => ({ ...m, content: (m.content ?? "") + tEvt.content }));
          return;
        }

        if (
          tEvt.type === "tool_call_delta" &&
          typeof tEvt.id === "string" &&
          typeof tEvt.name === "string" &&
          typeof tEvt.arguments_delta === "string"
        ) {
          patchAssistant(assistantId, (m) => {
            const list = Array.isArray(m.toolCalls) ? m.toolCalls : [];
            const idx = list.findIndex((x) => x.id === tEvt.id);
            if (idx === -1) {
              return {
                ...m,
                toolCalls: [
                  ...list,
                  {
                    id: tEvt.id,
                    name: tEvt.name,
                    argumentsText: tEvt.arguments_delta,
                    state: "building",
                  },
                ],
              };
            }
            const next = list.slice();
            const cur = next[idx]!;
            next[idx] = {
              ...cur,
              name: tEvt.name || cur.name,
              argumentsText: (cur.argumentsText ?? "") + tEvt.arguments_delta,
            };
            return { ...m, toolCalls: next };
          });
          return;
        }

        if (tEvt.type === "tool_call" && typeof tEvt.id === "string" && typeof tEvt.name === "string") {
          patchAssistant(assistantId, (m) => {
            const list = Array.isArray(m.toolCalls) ? m.toolCalls : [];
            const idx = list.findIndex((x) => x.id === tEvt.id);
            const next = list.slice();
            const base: ToolCallState =
              idx === -1
                ? {
                    id: tEvt.id,
                    name: tEvt.name,
                    argumentsText: typeof tEvt.arguments === "string" ? tEvt.arguments : "",
                    state: "running",
                  }
                : {
                    ...next[idx]!,
                    name: tEvt.name,
                    argumentsText:
                      typeof tEvt.arguments === "string" ? tEvt.arguments : next[idx]!.argumentsText,
                    state: "running",
                  };

            if (idx === -1) next.push(base);
            else next[idx] = base;
            return { ...m, toolCalls: next };
          });
          return;
        }

        if (tEvt.type === "tool_result" && typeof tEvt.id === "string" && typeof tEvt.name === "string") {
          patchAssistant(assistantId, (m) => {
            const list = Array.isArray(m.toolCalls) ? m.toolCalls : [];
            const idx = list.findIndex((x) => x.id === tEvt.id);
            if (idx === -1) {
              return {
                ...m,
                toolCalls: [
                  ...list,
                  {
                    id: tEvt.id,
                    name: tEvt.name,
                    argumentsText: "",
                    result: typeof tEvt.content === "string" ? tEvt.content : "",
                    state: "success",
                  },
                ],
              };
            }
            const next = list.slice();
            next[idx] = {
              ...next[idx]!,
              name: tEvt.name,
              result: typeof tEvt.content === "string" ? tEvt.content : next[idx]!.result,
              state: "success",
            };
            return { ...m, toolCalls: next };
          });
        }
      };

      try {
        await streamFinanceChat(
          {
            session_id: localSessionId ?? undefined,
            message: t,
            history_limit: 50,
          },
          {
            signal: controller.signal,
            onEvent,
          }
        );
      } catch (e) {
        if (controller.signal.aborted) return;
        const msg = e instanceof Error ? e.message : "Failed to send.";
        patchAssistant(assistantId, (m) => ({ ...m, content: msg }));
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        setIsStreaming(false);
        setStreamingAssistantId((cur) => (cur === assistantId ? null : cur));
      }
    },
    [patchAssistant, stopStreaming]
  );

  useLayoutEffect(() => {
    const text = (initialPrompt ?? "").trim();
    if (!text) return;

    const key = initialSeedKey;
    if (!key) return;

    let st = initialTurnStateByKey.get(key);
    if (!st) {
      st = { uiEnqueued: false, sendStatus: "idle", assistantId: makeId("a") };
      initialTurnStateByKey.set(key, st);
    }

    if (!st.uiEnqueued) {
      st.uiEnqueued = true;
      const userMsg: ChatMessage = { id: makeId("u"), role: "user", content: text };
      const assistantMsg: ChatMessage = {
        id: st.assistantId,
        role: "assistant",
        content: "",
        thinkingText: "",
        toolCalls: [],
      };
      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setInput("");
    }

    if (st.sendStatus === "sent" || st.sendStatus === "scheduled") return;
    st.sendStatus = "scheduled";
    setIsStreaming(true);
    setStreamingAssistantId(st.assistantId);

    let raf1 = 0;
    let raf2 = 0;

    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        const cur = initialTurnStateByKey.get(key);
        if (!cur || cur.sendStatus !== "scheduled") return;
        cur.sendStatus = "sent";
        void startChatTurn(text, { assistantId: cur.assistantId, skipEnqueue: true });
      });
    });

    return () => {
      if (raf1) window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
      const cur = initialTurnStateByKey.get(key);
      if (cur && cur.sendStatus === "scheduled") {
        cur.sendStatus = "idle";
      }
    };
  }, [initialPrompt, initialSeedKey, startChatTurn]);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-slate-100 text-slate-800">
      <FormControlStyles theme="light" />

      <div className="relative mx-auto w-full max-w-[1280px] px-8 shrink-0">
        <Header />
      </div>

      <main className="flex-1 overflow-y-auto px-4 md:px-8" ref={scrollRef}>
        <div className="mx-auto w-full max-w-[760px] pb-40 pt-6">
          {messages.map((m) =>
            m.role === "user" ? (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 8, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.2 }}
                className="mt-6 flex w-full justify-end"
              >
                <div className="flex max-w-[85%] items-start gap-3">
                  <div className="rounded-2xl rounded-tr-sm bg-white px-4 py-3 text-[14px] leading-relaxed text-slate-800 shadow-sm ring-1 ring-slate-900/5">
                    {m.content}
                  </div>
                  <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-slate-200 bg-slate-50 text-[11px] font-semibold text-slate-700">
                    NA
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="mt-8 flex w-full justify-start"
              >
                <div className="flex max-w-[90%] items-start gap-4">
                  <div className="relative grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-sm">
                    <div className="absolute inset-0 rounded-lg ring-1 ring-inset ring-black/10" />
                    <span className="text-[11px] font-bold">KQ</span>
                  </div>
                  <div className="flex flex-col gap-3 pt-1 w-full">
                    <AnimatePresence>
                      {((m.toolCalls?.length ?? 0) > 0 || (m.thinkingText ?? "").trim() || (isStreaming && streamingAssistantId === m.id)) && (
                        <motion.div
                          initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                          animate={{ opacity: 1, height: 160, marginBottom: 16 }}
                          exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                          transition={{ duration: 0.18 }}
                          className="relative w-full sm:w-[480px] overflow-hidden rounded-xl border border-slate-200/60 bg-slate-50/50 shadow-[inset_0_2px_8px_rgba(0,0,0,0.02)]"
                        >
                          <div className="absolute inset-0 overflow-y-auto px-4 py-3 custom-scrollbar">
                            <div className="mb-2 flex items-center gap-2 text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                              <Code2 className="h-3 w-3" />
                              <span>Execution Trace</span>
                            </div>

                            {(m.toolCalls?.length ?? 0) > 0 ? (
                              <motion.ul
                                variants={toolListVariants}
                                initial="hidden"
                                animate="show"
                                className="flex flex-col gap-2.5"
                              >
                                {(m.toolCalls ?? []).map((t) => {
                                  const argsSummary = summarizeToolArgs(t.argumentsText);
                                  const argsPreview = (t.argumentsText ?? "").slice(0, 240);
                                  const resultPreview = (t.result ?? "").slice(0, 240);
                                  return (
                                  <motion.li
                                    key={t.id}
                                    variants={toolItemVariants}
                                    className={
                                      "flex items-start gap-3 rounded-md px-2 py-1.5 transition-colors " +
                                      (t.state === "success"
                                        ? "opacity-60 grayscale"
                                        : "bg-white shadow-sm ring-1 ring-slate-900/5")
                                    }
                                  >
                                    <div className="pt-0.5">
                                      {t.state === "running" || t.state === "building" ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />
                                      ) : t.state === "success" ? (
                                        <Check className="h-3.5 w-3.5 text-slate-400" />
                                      ) : (
                                        <X className="h-3.5 w-3.5 text-red-500" />
                                      )}
                                    </div>
                                    <div className="flex flex-col min-w-0">
                                      <span className="font-mono text-[12px] font-medium text-slate-700 truncate">
                                        {t.name}
                                        {argsSummary ? <span className="text-slate-400">({argsSummary})</span> : null}
                                      </span>
                                      {t.state === "running" || t.state === "building" ? (
                                        <motion.span
                                          initial={{ opacity: 0.3 }}
                                          animate={{ opacity: 1 }}
                                          transition={{ repeat: Infinity, duration: 0.8, repeatType: "reverse" }}
                                          className="text-[11px] text-slate-500 mt-0.5"
                                        >
                                          Running...
                                        </motion.span>
                                      ) : null}
                                      {argsPreview ? (
                                        <div className="mt-1 whitespace-pre-wrap break-words font-mono text-[10px] leading-relaxed text-slate-500">
                                          {argsPreview}
                                        </div>
                                      ) : null}
                                      {t.state === "success" && resultPreview ? (
                                        <div className="mt-1 whitespace-pre-wrap break-words font-mono text-[10px] leading-relaxed text-slate-400">
                                          {resultPreview}
                                        </div>
                                      ) : null}
                                    </div>
                                  </motion.li>
                                  );
                                })}
                              </motion.ul>
                            ) : null}

                            {(m.thinkingText ?? "").trim() ? (
                              <div className="mt-3 text-[11px] leading-relaxed">
                                <MarkdownRenderer content={m.thinkingText ?? ""} theme="light" />
                              </div>
                            ) : null}
                          </div>
                          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-slate-50/90 to-transparent" />
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <div className="text-[15px] leading-relaxed text-slate-900">
                      <MessageContent content={m.content ?? ""} theme="light" />
                      {isStreaming && streamingAssistantId === m.id && (m.content ?? "") ? (
                        <span className="ml-0.5 inline-block w-2 animate-pulse text-slate-700">▍</span>
                      ) : null}
                    </div>
                  </div>
                </div>
              </motion.div>
            )
          )}
        </div>
      </main>

      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200/50 bg-slate-100/80 pb-4 pt-3 backdrop-blur-xl supports-[backdrop-filter]:bg-slate-100/60">
        <div className="mx-auto w-full max-w-[760px] px-8 relative">
          <div className="absolute right-8 top-0 -translate-y-full -mt-2 flex items-center gap-2">
            <button
              onClick={() => navigate('/strategies')}
              className="group flex items-center gap-1.5 rounded-full border border-slate-200/60 bg-white/50 px-3 py-1.5 text-[12px] font-medium text-slate-500 shadow-sm backdrop-blur-sm transition-all hover:bg-white hover:text-slate-800 hover:shadow"
              type="button"
            >
              <Sparkles className="h-3.5 w-3.5 transition-transform duration-300 group-hover:scale-110" />
              <span className="hidden sm:inline">Try this idea in quantflow</span>
            </button>
            <button
              onClick={handleReset}
              disabled={messages.length === 0 && !isStreaming}
              className="group flex items-center gap-1.5 rounded-full border border-slate-200/60 bg-white/50 px-3 py-1.5 text-[12px] font-medium text-slate-500 shadow-sm backdrop-blur-sm transition-all hover:bg-white hover:text-slate-800 hover:shadow disabled:pointer-events-none disabled:opacity-40"
              type="button"
            >
              <RotateCcw className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-rotate-180" />
              <span className="hidden sm:inline">Reset Chat</span>
            </button>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <ChatBar
              Icon={ArrowUp}
              value={input}
              onChange={setInput}
              onSend={(t) => startChatTurn(t)}
              placeholder="Ask K-Quant to analyze market data..."
              disabled={isStreaming}
            />
            <DemoPrompts onSelectPrompt={(prompt) => {
              if (!isStreaming) {
                setInput(prompt);
              }
            }} />
            <div className="mt-3 text-center text-[11px] font-medium text-slate-400">
              K-Quant may produce inaccurate insights. Double-check important financials.
              Results are for presentation purposes only and does not constitute any investment advice. The stock market is risky; invest with caution!
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
